// library-model.js — pure decisions behind Liked Songs, podcasts and
// "Keep it going" on the music card. No React, no fetches: the callers in
// states.jsx / shell.jsx do the I/O and render, this decides what to play,
// what to show and what to ask for. Tests: src/dev/library-model.test.mjs.

// ── Liked Songs ──────────────────────────────────────────────────────────
// Sonos plays tracks, not Spotify's "Liked Songs" collection, so a Liked
// Songs play is the first track dispatched plus the rest queued (the same
// path the artist view uses). Capped so one tap never queues hundreds of
// enqueue calls.
export const LIKED_QUEUE_MAX = 50;

// Tracks to play for a tap on row `startIndex` (that track then the next
// ones), or for Shuffle (a random sample). Returns [] when nothing playable.
export function likedPlayList(tracks, { startIndex = 0, shuffle = false, max = LIKED_QUEUE_MAX, random = Math.random } = {}) {
  const list = (tracks || []).filter((t) => t && t.id);
  if (!list.length) return [];
  if (!shuffle) return list.slice(Math.max(0, startIndex), Math.max(0, startIndex) + max);
  const pool = list.slice();
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }
  return pool.slice(0, max);
}

// ── Podcasts ─────────────────────────────────────────────────────────────
// Spotify's resume_point is the listener's own progress, kept by Spotify
// across devices (it needs the user-read-playback-position scope, which the
// HA token carries). Whether Sonos playback writes back to it is untested.

const MIN = 60 * 1000;

export function episodeProgress(ep) {
  const rp = ep?.resume_point;
  const dur = ep?.duration_ms || 0;
  if (rp?.fully_played) return { state: 'played', leftMs: 0 };
  const pos = rp?.resume_position_ms || 0;
  if (pos > 0) return { state: 'in-progress', leftMs: Math.max(0, dur - pos), positionMs: pos };
  return { state: 'new', leftMs: dur };
}

// "1 h 12 min left", "8 min left", "Played", or '' for an unstarted one.
export function progressLabel(ep) {
  const p = episodeProgress(ep);
  if (p.state === 'played') return 'Played';
  if (p.state !== 'in-progress') return '';
  const mins = Math.max(1, Math.round(p.leftMs / MIN));
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h ? `${h} h${m ? ` ${m} min` : ''} left` : `${m} min left`;
}

// Where to start an episode, in ms. Resume only when there is a real point
// to resume from and a real amount left; otherwise start at the top.
export function resumeMs(ep) {
  const p = episodeProgress(ep);
  if (p.state !== 'in-progress') return 0;
  if (p.positionMs < 30 * 1000 || p.leftMs < MIN) return 0;
  return p.positionMs;
}

// From [{ show, episodes }] build the two home rows. Episodes get their show
// attached (episode objects from /shows/{id}/episodes don't carry it).
export function podcastRows(showsWithEpisodes, { now = Date.now(), newDays = 14, max = 12 } = {}) {
  const all = [];
  for (const { show, episodes } of showsWithEpisodes || []) {
    for (const ep of episodes || []) {
      if (ep && ep.id) all.push({ ...ep, show: ep.show || show });
    }
  }
  const byDateDesc = (a, b) => String(b.release_date || '').localeCompare(String(a.release_date || ''));
  const cutoff = now - newDays * 24 * 60 * MIN;
  const continueListening = all
    .filter((ep) => episodeProgress(ep).state === 'in-progress' && resumeMs(ep) > 0)
    .sort(byDateDesc).slice(0, max);
  const fresh = all
    .filter((ep) => episodeProgress(ep).state === 'new'
      && ep.release_date && Date.parse(ep.release_date) >= cutoff)
    .sort(byDateDesc).slice(0, max);
  return { continueListening, fresh };
}

// ── Keep it going ────────────────────────────────────────────────────────
// Spotify's own "more like this" (recommendations, related artists) is
// closed to Development Mode apps (404 / 403, checked 2026-10-31), so the
// builder's Claude step extends the queue instead: seeds are the tracks at
// the end of the queue, exclusions are everything already in it.
export function keepGoingRequest(queueItems, { seedCount = 10, target = 15 } = {}) {
  const titled = (queueItems || [])
    .map((it) => ({ title: (it?.media_title || '').trim(), artist: (it?.media_artist || '').trim() }))
    .filter((x) => x.title);
  return {
    seeds: titled.slice(-seedCount),
    exclude: titled,
    target_length: target,
  };
}

// Song identity across recordings: title without "- Remastered 2011" /
// "(Live)" tails, plus the first artist, lowercased and stripped.
export function songKey(title, artist) {
  const norm = (x) => String(x || '').toLowerCase().normalize('NFKD').replace(/[\u0300-\u036f]/g, '')
    .replace(/\s*[([].*?[)\]]\s*/g, ' ').replace(/\s+-\s.*$/, '')
    .replace(/[^a-z0-9 ]+/g, '').replace(/\s+/g, ' ').trim();
  const first = String(artist || '').split(/,|&| feat\.? | ft\.? /i)[0];
  return `${norm(title)}|${norm(first)}`;
}

// Drop resolved tracks already in the queue, by Spotify id or by song (a
// remaster of a queued song is still a repeat; Claude ignored the exclusion
// list for one of fifteen in the 2026-10-31 dry run). Keeps order.
export function freshTracks(resolved, queueSpotifyIds, queueSongs = []) {
  const seenIds = new Set(queueSpotifyIds || []);
  const seenSongs = new Set((queueSongs || []).map((q) => songKey(q.title, q.artist)));
  const out = [];
  for (const t of resolved || []) {
    if (!t?.id || seenIds.has(t.id)) continue;
    const key = t.title ? songKey(t.title, t.artist) : null;
    if (key && seenSongs.has(key)) continue;
    seenIds.add(t.id);
    if (key) seenSongs.add(key);
    out.push(t);
  }
  return out;
}
